import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { FileTypeEnum } from '@repo/toolkit/types'
import { UploadIcon } from '@repo/ui/icons'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import {
  TransportTypeEnum,
  transportTypeMapping
} from '../../../lib/types/enums/trip/trip-type.enum'
import { TripBookingDocumentInput } from '../../../lib/types/models/trip-booking/trip-booking-document-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledFileInput from '../../elements/inputs/controlled-file-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<TripBookingDocumentInput> & {
  className?: string
}

const formSchema: ZodType<TripBookingDocumentInput> = z.object({
  name: createStringSchema('NOM'),
  directionality: createEnumSchema({ fieldName: 'Type', enumType: TransportTypeEnum }),
  file: FILE_VALIDATION({ error: 'Veuillez télécharger un fichier valide' })
})

const TripBookingDocumentForm = ({
  className,
  defaultValues,
  ...validationButtonProps
}: Props) => {
  const form = useForm<TripBookingDocumentInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', className)}>
          <FormField
            control={form.control}
            name={'name'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput {...field} label="NOM" type="text" required />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'directionality'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput<TransportTypeEnum>
                    {...field}
                    required
                    label={'Type'}
                    options={Object.values(TransportTypeEnum).map((type) => ({
                      label: transportTypeMapping[type].label,
                      value: type
                    }))}
                    selectedOption={getSelectedEnumOption(field.value, transportTypeMapping)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'file'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledFileInput
                    required
                    placeholder="Glisser-déposer ou parcourir"
                    types={[FileTypeEnum.IMAGE, FileTypeEnum.PDF]}
                    icon={UploadIcon}
                    label={'Télécharger le billet'}
                    files={field?.value ? [field?.value] : undefined}
                    onFilesChange={(files) => {
                      if (files.length === 0) return field.onChange(null)
                      const file = files[0]
                      field.onChange(file)
                      if (!form.watch('name')) {
                        form.setValue('name', file.name)
                        form.trigger('name')
                      }
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default TripBookingDocumentForm
